import { styled } from '@mui/material';
import { Box, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useProductCatalogsQuery } from '@features/redux/apis/productCatalog';
import type { ProductCatalog } from '@/types/productCatalog';

/** Maps numeric CutProfile ID → BC component SKU */
const CUT_PROFILE_TO_SKU: Record<number, string> = {
    1: '03-000003',
    2: '03-000002',
    3: '03-000037',
    7: '03-000004',
    8: '03-000033',
    9: '03-000035',
    10: '03-000036',
    11: '03-000039',
    12: '03-000038',
    14: '03-000040',
};

const StyledSlider = styled(Slider)(({ theme }) => ({
    height: 8,
    padding: '16px 0',
    '& .MuiSlider-track': {
        border: 0,
        backgroundColor: theme.palette.primary.main,
    },
    '& .MuiSlider-rail': {
        opacity: 0.15,
        backgroundColor: theme.palette.grey[300],
    },
    '& .MuiSlider-thumb': {
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
        '&:focus, &:hover, &.Mui-active': {
            boxShadow: 'inherit',
        },
    },
}));

const MODEL_STEPS: {
    /** MachineVariant tokens this range applies to — the unambiguous identity */
    variants: string[];
    /** Legacy ProductID fallback for stations that haven't re-registered */
    productIds: number[];
    step: number;
    min: number;
    max: number;
    material: Record<number, { default: number }>;
    range: string;
}[] = [
    {
        variants: ['Graphtec6000', 'Graphtec6000Plus', 'GraphTec7000'],
        productIds: [4386, 4400, 4405],
        step: 1,
        min: -20,
        max: 20,
        material: {
            1: { default: 14 },   // Original - 6mil Gloss 60D
            2: { default: 14 },   // Original Matte
            3: { default: 14 },   // Original Air
            7: { default: 25 },   // Curved Fusion Film Roll
            8: { default: 14 },   // Privacy Film
            9: { default: 21 },   // Fusion Pro
            10: { default: 14 },  // Recycled Film (EPU Green)
            11: { default: 14 },  // Original Blue
            12: { default: 21 },  // Pro Matte
            14: { default: 21 },  // 03-000040
        },
        range: '1-40',
    },
    {
        variants: ['RCMini'],
        productIds: [67],
        step: 5,
        min: -200,
        max: 200,
        material: {
            1: { default: 250 },   // Original - 6mil Gloss 60D
            2: { default: 250 },   // Original Matte
            3: { default: 250 },   // Original Air
            7: { default: 310 },   // Curved Fusion Film Roll
            8: { default: 250 },   // Privacy Film
            9: { default: 310 },   // Fusion Pro
            10: { default: 250 },  // Recycled Film (EPU Green)
            11: { default: 250 },  // Original Blue
            12: { default: 310 },  // Pro Matte
            14: { default: 310 },  // 03-000040
        },
        range: '1-1000',
    },
    {
        variants: ['RC3.0'],
        productIds: [10],
        step: 1,
        min: -40,
        max: 40,
        material: {
            1: { default: 36 },   // Original - 6mil Gloss 60D
            2: { default: 36 },   // Original Matte
            3: { default: 36 },   // Original Air
            7: { default: 50 },   // Curved Fusion Film Roll
            8: { default: 36 },   // Privacy Film
            9: { default: 44 },   // Fusion Pro
            10: { default: 36 },  // Recycled Film (EPU Green)
            11: { default: 36 },  // Original Blue
            12: { default: 44 },  // Pro Matte
            14: { default: 44 },  // 03-000040
        },
        range: '1-100',
    },
    {
        // TODO: confirm correct offset range for Bamboo (RC 4.0) — copied from RCMini,
        // whose gram-force scale Bamboo shares
        variants: ['Bamboo'],
        productIds: [],
        step: 5,
        min: -200,
        max: 200,
        material: {
            1: { default: 250 },   // Original - 6mil Gloss 60D
            2: { default: 250 },   // Original Matte
            3: { default: 250 },   // Original Air
            7: { default: 310 },   // Curved Fusion Film Roll
            8: { default: 250 },   // Privacy Film
            9: { default: 310 },   // Fusion Pro
            10: { default: 250 },  // Recycled Film (EPU Green)
            11: { default: 250 },  // Original Blue
            12: { default: 310 },  // Pro Matte
            14: { default: 310 },  // 03-000040
        },
        range: '1-1000',
    },
];
type ForceMaterialsState = null | Record<
    string,
    {
        materials: Record<string, { forceOffset: number }>;
    }
>;

interface ForceMaterialsProps {
    productId: string;
    /** Kiosk-resolved MachineVariant — preferred over productId for ranges and config keys */
    machineVariant?: string | null;
    unlocked: boolean;
    state: ForceMaterialsState;
    dispatch: (value: ForceMaterialsState) => void;
}

export const ForceMaterials = ({ productId, machineVariant, unlocked, state, dispatch }: ForceMaterialsProps) => {
    const stepMap = useMemo(
        () =>
            (machineVariant && MODEL_STEPS.find((entry) => entry.variants.includes(machineVariant))) ||
            MODEL_STEPS.find((entry) => entry.productIds.includes(Number(productId))) ||
            MODEL_STEPS[0],
        [machineVariant, productId]
    );

    // Configs are keyed by MachineVariant; v5.3+ kiosks read json[machineVariant] || json[productId],
    // so variant writes win over stale legacy productId entries. Stations that never reported a
    // variant keep writing the productId key their kiosks read.
    const configWriteKey = machineVariant || productId;
    const savedMaterials =
        (machineVariant ? state?.[machineVariant]?.materials : undefined) ?? state?.[productId]?.materials;

    /* ---------------------------------------------------------------------- */
    /*                       MATERIAL TAB / TOGGLE GROUP                      */
    /* ---------------------------------------------------------------------- */
    const materialIds = useMemo(
        () => Object.keys(stepMap.material),
        [stepMap]
    );
    const [selected, setSelected] = useState<string>('1');

    /* ---------------------------------------------------------------------- */
    /*                     LINKED PRODUCT CATALOG ITEMS                       */
    /* ---------------------------------------------------------------------- */
    const { data: products } = useProductCatalogsQuery({});

    const productsByCutProfile = useMemo(() => {
        if (!products) return {} as Record<number, ProductCatalog[]>;
        return products.reduce<Record<number, ProductCatalog[]>>((acc, product) => {
            if (product.CutProfile == null) return acc;
            (acc[product.CutProfile] ??= []).push(product);
            return acc;
        }, {});
    }, [products]);

    const linkedProductNames = useMemo(() => {
        const items = productsByCutProfile[+selected] ?? [];
        const seen = new Set<string>();
        return items.reduce<{ name: string; line: string }[]>((acc, p) => {
            const key = `${p.ProductName}|${p.ProductLine}`;
            if (!seen.has(key)) {
                seen.add(key);
                acc.push({ name: p.ProductName, line: p.ProductLine });
            }
            return acc;
        }, []);
    }, [productsByCutProfile, selected]);

    const forceOffset = savedMaterials?.[selected]?.forceOffset ?? 0;
    const defaultVal = stepMap.material[+selected]?.default ?? 0;

    const handleSlider = (_: any, value: number | number[]) => {
        dispatch({
            ...state,
            [configWriteKey]: {
                materials: {
                    ...(savedMaterials ?? {}),
                    [selected]: { forceOffset: value as number },
                },
            },
        });
    };

    const getMaterialSku = (id: string): string => CUT_PROFILE_TO_SKU[+id] ?? id;

    return (
        <Box>
            {/* Material selector ------------------------------------------------ */}
            <ToggleButtonGroup value={selected} exclusive onChange={(_, val) => val && setSelected(val)} sx={{ mb: 2 }}>
                {materialIds.map((id) => (
                    <ToggleButton key={id} value={id} sx={{ textTransform: 'none', p: '6px 16px' }}>
                        <Typography variant='body2' fontWeight={selected === id ? 700 : 500}>
                            {getMaterialSku(id)}
                        </Typography>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {/* Linked products --------------------------------------------------- */}
            <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                Products
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.primary', mb: 2 }}>
                {linkedProductNames.length > 0
                    ? linkedProductNames.map((p) => `${p.line} ${p.name}`).join(', ')
                    : 'No products linked'}
            </Typography>

            {/* Label ------------------------------------------------------------- */}
            <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                Offset (Default: {defaultVal})
            </Typography>

            {/* Slider ------------------------------------------------------------ */}
            <StyledSlider
                title={`${getMaterialSku(selected)} Force Offset`}
                valueLabelDisplay={'on'}
                disabled={!unlocked}
                value={forceOffset}
                min={stepMap.min}
                max={stepMap.max}
                step={stepMap.step}
                onChange={handleSlider}
            />

            {/* Min / Max labels -------------------------------------------------- */}
            <Stack direction='row' justifyContent='space-between' mt={0.5}>
                <Typography variant='caption' color='text.primary'>
                    -{Math.abs(stepMap.min)}
                </Typography>
                <Typography variant='caption' color='text.primary'>
                    +{stepMap.max}
                </Typography>
            </Stack>
        </Box>
    );
};
